import cs from "classnames";
import React from "react";
import {
	GoChevronDown,
	GoChevronLeft,
	GoChevronRight,
	GoChevronUp
} from "react-icons/go";
import { MdOutlineSearch } from "react-icons/md";
import { VscCollapseAll, VscExpandAll } from "react-icons/vsc";
import { defineMessages, useIntl } from "react-intl";

import { useSidebarState } from "src/common/hooks/useSidebarState";
import { Permission, useHasPermission } from "src/features/auth";
import { FormTree } from "src/modules/shared/form-explorer/FormTree";
import { LeafNode, ParentNode } from "src/modules/shared/form-explorer/types";
import { useState } from "src/modules/shared/form-explorer/useState";

import "./FormExplorer.css";

const messages = defineMessages({
	search: {
		id: "right_bar.search",
		defaultMessage: "Keresés az űrlapok között"
	},
	collapseAll: {
		id: "right_bar.collapse_all",
		defaultMessage: "Összes bezárása"
	},
	expandAll: {
		id: "right_bar.expand_all",
		defaultMessage: "Összes kinyitása"
	}
});

interface FormExplorerProps {
	forms: (ParentNode | LeafNode)[];
}

const IS_COLLAPSED_KEY = "isFormExplorerCollapsed";

export const FormExplorer: React.FunctionComponent<FormExplorerProps> = ({
	forms
}) => {
	const { isMobile, isCollapsed, onToggle } = useSidebarState(
		IS_COLLAPSED_KEY,
		false
	);

	const { isFocused, onFocus, onBlur, query, onChange } = useSearchState();
	const hasLimitedAccess = useHasPermission(Permission.LimitedAccessFormRW);
	const [nodes, dispatch] = useState(forms, hasLimitedAccess, query);

	const onCollapseAll = React.useCallback(() => {
		dispatch({ type: "collapse-all" });
	}, [dispatch]);

	const onExpandAll = React.useCallback(() => {
		dispatch({ type: "expand-all" });
	}, [dispatch]);

	const { formatMessage } = useIntl();

	return (
		<div
			className={cs("form-explorer", {
				"form-explorer--collapsed": isCollapsed
			})}
		>
			<button className="form-explorer__toggle" onClick={onToggle}>
				{isMobile ? (
					isCollapsed ? (
						<GoChevronDown />
					) : (
						<GoChevronUp />
					)
				) : isCollapsed ? (
					<GoChevronLeft />
				) : (
					<GoChevronRight />
				)}
			</button>
			<div className="form-explorer__header">
				<div
					className={cs("form-explorer__search", {
						"form-explorer__search--focused": isFocused
					})}
				>
					<MdOutlineSearch />
					<input
						type="text"
						placeholder={formatMessage(messages.search)}
						value={query}
						onFocus={onFocus}
						onBlur={onBlur}
						onChange={onChange}
					/>
				</div>
				<button
					className="form-explorer__button"
					title={formatMessage(messages.collapseAll)}
					onClick={onCollapseAll}
				>
					<VscCollapseAll />
				</button>
				<button
					className="form-explorer__button"
					title={formatMessage(messages.expandAll)}
					onClick={onExpandAll}
				>
					<VscExpandAll />
				</button>
			</div>
			<div className="form-explorer__body">
				<div className="form-explorer__form-tree">
					<FormTree
						isForceOpen={!!query}
						nodes={nodes}
						dispatch={dispatch}
					/>
				</div>
			</div>
		</div>
	);
};

function useSearchState(): {
	isFocused: boolean;
	onFocus(): void;
	onBlur(): void;
	query: string;
	onChange(event: React.ChangeEvent<HTMLInputElement>): void;
} {
	const [isFocused, setFocused] = React.useState(false);

	const onFocus = React.useCallback(() => {
		setFocused(true);
	}, []);

	const onBlur = React.useCallback(() => {
		setFocused(false);
	}, []);

	const [query, setQuery] = React.useState<string>("");

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setQuery(event.target.value);
		},
		[]
	);

	return { isFocused, onFocus, onBlur, query, onChange };
}
